import { axiosDZAuthInstance } from '../axios'
import { DASHBOARD } from '../routes'
import { formatErrorMessage } from '../helpers'
import { EResource } from '@/admin/types'
import type {
  TDeactivationWarnings,
  TSupplierDetail,
  TSupplierStats,
} from '@/modules/Suppliers/types'

/**
 * GET /dashboard/suppliers/{code}
 */
export const getSupplierByCode = async (code: string): Promise<TSupplierDetail> => {
  const { data } = await axiosDZAuthInstance.get<TSupplierDetail>(
    `${DASHBOARD()}/${EResource.Suppliers}/${encodeURIComponent(code)}`,
  )
  return data
}

/**
 * GET /dashboard/suppliers/{code}/stats
 */
export const getSupplierStats = async (code: string): Promise<TSupplierStats> => {
  const { data } = await axiosDZAuthInstance.get<TSupplierStats>(
    `${DASHBOARD()}/${EResource.Suppliers}/${encodeURIComponent(code)}/stats`,
  )
  return data
}

/**
 * POST /dashboard/suppliers/{code}/activate
 */
export const activateSupplier = async (code: string): Promise<void> => {
  try {
    await axiosDZAuthInstance.post(
      `${DASHBOARD()}/${EResource.Suppliers}/${encodeURIComponent(code)}/activate`,
      { confirm: true },
    )
  } catch (error) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const rawMessage = err.response?.data?.message
    const message = rawMessage
      ? formatErrorMessage(rawMessage as Parameters<typeof formatErrorMessage>[0])
      : 'Failed to activate supplier'
    throw new Error(message)
  }
}

/**
 * POST /dashboard/suppliers/{code}/deactivate
 */
export const deactivateSupplier = async (code: string, reason?: string): Promise<void> => {
  try {
    await axiosDZAuthInstance.post(
      `${DASHBOARD()}/${EResource.Suppliers}/${encodeURIComponent(code)}/deactivate`,
      { confirm: true, ...(reason ? { reason } : {}) },
    )
  } catch (error) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const rawMessage = err.response?.data?.message
    const message = rawMessage
      ? formatErrorMessage(rawMessage as Parameters<typeof formatErrorMessage>[0])
      : 'Failed to deactivate supplier'
    throw new Error(message)
  }
}

/**
 * GET /dashboard/suppliers/{code}/deactivation-warnings
 */
export const getDeactivationWarnings = async (
  code: string,
): Promise<TDeactivationWarnings> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TDeactivationWarnings>(
      `${DASHBOARD()}/${EResource.Suppliers}/${encodeURIComponent(code)}/deactivation-warnings`,
    )
    return data
  } catch (error) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const rawMessage = err.response?.data?.message
    const message = rawMessage
      ? formatErrorMessage(rawMessage as Parameters<typeof formatErrorMessage>[0])
      : 'Failed to fetch deactivation warnings'
    throw new Error(message)
  }
}
